/**
 * OAuth consent return-flow (Phase B). When an MCP client (e.g. Claude) sends
 * the user to the backend AS `/oauth/authorize` while they are NOT logged in,
 * the backend 302s to `${ORIGIN}/?next=<encoded /oauth/authorize?...>`. The SPA
 * loads here; once the user is authenticated we send them back to the AS so the
 * consent screen can render and a code can be minted.
 *
 * SECURITY (open-redirect defense): `next` is accepted ONLY when it is a
 * same-origin RELATIVE path to our own `/oauth/authorize` endpoint. Anything
 * else — a foreign origin, a protocol-relative `//host`, a different path — is
 * ignored. `window.location.replace` resolves a leading-slash path against the
 * current origin, so a validated `/oauth/authorize?...` can never leave the site.
 */
import { Auth } from './auth';

/**
 * Return a safe same-origin AS path, or null if `raw` is not trustworthy.
 * `raw` is the value from `URLSearchParams.get('next')`, which has ALREADY been
 * percent-decoded exactly once (and equals the backend's `req.url`). We do NOT
 * decode again — a second decodeURIComponent would corrupt the nested
 * (still-encoded) redirect_uri and is a classic double-decode smuggling vector.
 */
export function safeOauthNext(raw: string | null): string | null {
  if (!raw) return null;
  // Must be EXACTLY our AS authorize path (relative, single leading slash).
  if (raw !== '/oauth/authorize' && !raw.startsWith('/oauth/authorize?')) return null;
  // Belt-and-suspenders (already implied by the prefix): no protocol-relative,
  // no scheme, no backslash.
  if (raw.startsWith('//') || raw.includes('\\') || /^[a-z][a-z0-9+.-]*:/i.test(raw)) return null;
  return raw;
}

/**
 * If the current URL carries a valid `?next=/oauth/authorize…`, drive the
 * return: redirect immediately when authed, otherwise open login and redirect
 * as soon as the user signs in. No-op when there is no (valid) `next`.
 * Call once after `Auth.init()` resolves.
 */
export function handleOAuthReturn(): void {
  if (typeof window === 'undefined') return;
  const next = safeOauthNext(new URLSearchParams(window.location.search).get('next'));
  if (!next) return;

  const go = (): void => { window.location.replace(next); };

  if (Auth.isAuthed()) { go(); return; }

  // Not logged in → register the redirect FIRST, then prompt login. A full
  // navigation (replace) supersedes any client-side pushState the auth modal
  // performs, so the final destination is always the AS authorize endpoint.
  const off = Auth.onChange((u) => {
    if (u) { off(); go(); }
  });
  window.dispatchEvent(new CustomEvent('roamzy:open-login'));
}
